import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { catchError, of, switchMap, timer } from 'rxjs';

import { NotificationApiService } from '../notification-api.service';
import { SystemAnnouncementV1 } from '../../../api/models';
import { selectUserProfile } from '../../../authentication/state/selector';

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const DISMISSED_KEY_PREFIX = 'slm-maintenance-dismissed-';

/**
 * In-app banner for planned maintenance windows (#48894), polled every 5 minutes.
 * Dismissal is sessionStorage-based, keyed per user so accounts sharing a browser don't hide each other's banner.
 */
@Component({
  selector: 'app-maintenance-banner',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './maintenance-banner.component.html',
  styleUrl: './maintenance-banner.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaintenanceBannerComponent implements OnInit {
  private readonly api = inject(NotificationApiService);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  readonly announcements = signal<SystemAnnouncementV1[]>([]);
  private readonly userId = signal<string | null>(null);
  private readonly dismissedIds = signal<ReadonlySet<string>>(new Set());

  readonly visibleAnnouncements = computed(() =>
    this.announcements().filter((a) => !this.dismissedIds().has(a.id ?? ''))
  );

  ngOnInit(): void {
    this.store
      .select(selectUserProfile)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((profile) => {
        this.userId.set(profile?.id ?? null);
        this.dismissedIds.set(this.readDismissed());
      });

    timer(0, POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this.api.getActiveMaintenance().pipe(catchError(() => of([] as SystemAnnouncementV1[])))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((items) => this.announcements.set(items ?? []));
  }

  onDismiss(announcement: SystemAnnouncementV1): void {
    if (!announcement.id) return;
    try {
      sessionStorage.setItem(this.keyPrefix() + announcement.id, '1');
    } catch {
      // Storage unavailable (private mode etc.) — dismissal still applies for this view.
    }
    this.dismissedIds.update((ids) => new Set([...ids, announcement.id!]));
  }

  windowLabel(a: SystemAnnouncementV1): string {
    const fmt = (iso: string | null | undefined) => {
      if (!iso) return '';
      const d = new Date(iso);
      return Number.isNaN(d.getTime())
        ? ''
        : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    };
    const from = fmt(a.startAt);
    const to = fmt(a.endAt);
    return from && to ? `${from} – ${to}` : '';
  }

  private keyPrefix(): string {
    const user = this.userId();
    return user ? `${DISMISSED_KEY_PREFIX}${user}-` : DISMISSED_KEY_PREFIX;
  }

  private readDismissed(): ReadonlySet<string> {
    const ids = new Set<string>();
    const prefix = this.keyPrefix();
    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(prefix)) ids.add(key.slice(prefix.length));
      }
    } catch {
      // Storage unavailable — start with nothing dismissed.
    }
    return ids;
  }
}
